/**
 * Mocks over MCP (PLAN §E / §H tranche 4) — two tools over one field.
 *
 * A mock lives at `request.mock.responseExamples[]`; there is no table of its
 * own. So both tools are ordinary request writes and they ride tranche 3's
 * machinery whole: the one write path, `expected_rev` on the request, the
 * §F2 tab patch, the 32 KB pre-check.
 *
 * **Kept in its own file rather than merged into `collectionUpdates.js`**,
 * which PLAN §E allowed. The reason is the one the conventions give for
 * modular files: these two tools are the only place in the feature that writes
 * a RESPONSE-SHAPED payload, and the argument about why that is safe (below) is
 * worth having somewhere a reader can find it rather than buried among four
 * unrelated updates.
 *
 * ## Why writing a mock body is allowed when reading a response body is not
 *
 * §C refuses response bodies because a REAL response is post-resolution
 * content the app captured from a live server, and a login response IS a
 * credential. A mock example is the opposite artefact in every respect that
 * matters: the agent supplies it, it never came from a server, and this server
 * has no way to obtain one. Nothing is read here — the write is one direction.
 *
 * The read side of a mock stays as tranche 2 left it and does not soften:
 * `projectMockExamples` returns `{id, name, status, contentType, bytes,
 * enabled, sourceType, matchQuery NAMES, matchHeaders NAMES}` and never the
 * body, because "fake" is a claim nobody verified and real captured payloads
 * end up in that field. **So an agent can write a mock body it cannot read
 * back**, which is deliberate and is stated in the tool descriptions.
 *
 * ## What is NOT here
 *
 * * **Capturing from the last real response.** The in-app AI's
 *   `create_mock_example` copies the captured body in-process; MCP does not get
 *   it, because it is the one mock path that touches a response (§C). An agent
 *   that wants a realistic example writes one from the spec — which is exactly
 *   what the owner described.
 * * **Starting, stopping or reloading the mock server (O31).** It binds a
 *   port, has no authentication and allows any origin, by the app's own tool
 *   contract. "Collections: write" does not describe "may open a network
 *   listener", and `mockServerService` is a pinned needle no MCP module may
 *   name.
 * * **Generating from a schema (O35).** `sourceType: 'schema'` exists in the
 *   record and the Rust mock server honours it, but schema CREATION is two more
 *   families and is out of this phase, so an MCP-written example is always
 *   `sourceType: 'example'` with a body the agent wrote.
 */
import { mcpError } from '../errors';
import { checkKvRows, checkText } from '../collectionRecords';
import { applyCollectionsChange, findRequest, requireRev } from '../collectionsDb';
import { revOfRequest } from '../collectionRev';
import { tabPatchFromRequest } from '../requestTabSync';
import { generateLocalEntityId } from '../../utils/localIds';

function requestNotFound(requestId) {
  return mcpError(
    'not_found',
    'No request with that id in the open workspace. Nothing was written. Call collections_list with include_requests and use an id it returns.',
    { requestId: String(requestId ?? '') }
  );
}

const examplesOf = (request) =>
  Array.isArray(request?.mock?.responseExamples) ? request.mock.responseExamples : [];

/** The record shape `mock_server.rs:20-47` reads and `normalizeMockExample`
 *  (`utils/workspaceTabPersistence.js:138-155`) writes. Every field is set —
 *  a field left out of a record does not default, it stops existing on the
 *  next whole-snapshot save. */
function buildExample(args) {
  const rows = (list) =>
    (Array.isArray(list) ? list : [])
      .filter((r) => typeof r?.key === 'string' && r.key.trim())
      .map((r) => ({
        key: String(r.key),
        value: typeof r.value === 'string' ? r.value : '',
        enabled: r.enabled !== false,
      }));
  return {
    id: generateLocalEntityId('mock'),
    name: typeof args.name === 'string' && args.name.trim() ? args.name.trim() : 'Example',
    status: Number.isInteger(args.status) ? args.status : 200,
    contentType:
      typeof args.content_type === 'string' && args.content_type.trim()
        ? args.content_type.trim()
        : 'application/json',
    headers: [],
    body: typeof args.body === 'string' ? args.body : '',
    matchQuery: rows(args.match_query),
    matchHeaders: rows(args.match_headers),
    enabled: true,
    // **O35.** Always 'example': schema-generated mocks need a schema, and
    // schema creation is out of this phase.
    sourceType: 'example',
    schemaId: null,
  };
}

/** What a result reports about an example. The BODY is never in it — the same
 *  rule the read projection follows, applied to the tool that wrote it. */
function exampleReport(example) {
  return {
    id: String(example.id),
    name: String(example.name || ''),
    status: Number(example.status) || 0,
    contentType: String(example.contentType || ''),
    // A BYTE COUNT, never the body — `create_mock_example`'s own precedent
    // (`toolSchemas.js:762`: "Returns ids, status and a byte count, never the
    // body"), taken verbatim.
    bytes: new TextEncoder().encode(String(example.body ?? '')).length,
    enabled: example.enabled !== false,
    sourceType: example.sourceType === 'schema' ? 'schema' : 'example',
    matchQuery: (example.matchQuery || []).map((r) => String(r.key)),
    matchHeaders: (example.matchHeaders || []).map((r) => String(r.key)),
  };
}

export async function mockExampleCreate(args = {}) {
  if (typeof args.body !== 'string') {
    return mcpError('invalid_argument', '`body` is required and must be a string.');
  }
  const refusal =
    // 20 000, under the 32 KB RECORD cap on purpose: a field cap fires first
    // and says something a model can act on ("send less content in `body`")
    // rather than `record_too_large`, which names the whole request.
    checkText(args.body, 'body', 20000) ||
    checkText(args.name, 'name', 200) ||
    checkText(args.content_type, 'content_type', 200) ||
    checkKvRows(args.match_query, 'match_query') ||
    checkKvRows(args.match_headers, 'match_headers');
  if (refusal) return refusal;
  if (args.status != null && (!Number.isInteger(args.status) || args.status < 100 || args.status > 599)) {
    return mcpError('invalid_argument', '`status` must be an HTTP status code between 100 and 599.');
  }

  return applyCollectionsChange((collections) => {
    const found = findRequest(collections, args.request_id);
    if (!found) return { error: requestNotFound(args.request_id) };

    const stale = requireRev(args.expected_rev, revOfRequest(found.request), 'that request');
    if (stale) return { error: stale };

    const example = buildExample(args);
    const nextRequest = {
      ...found.request,
      mock: {
        ...(found.request.mock || {}),
        // Append. Never rebuild the list — the same rule that governs the
        // collections array itself, one level down.
        responseExamples: [...examplesOf(found.request), example],
      },
    };

    return {
      next: collections.map((c) =>
        String(c.id) === String(found.collection.id)
          ? {
              ...c,
              requests: (c.requests || []).map((r) =>
                String(r.id) === String(nextRequest.id) ? nextRequest : r
              ),
            }
          : c
      ),
      sizedRecords: [nextRequest],
      tabPatches: [{ requestId: nextRequest.id, patch: tabPatchFromRequest(nextRequest) }],
      result: {
        created: 'mock_example',
        collectionId: String(found.collection.id),
        requestId: String(nextRequest.id),
        example: exampleReport(example),
        exampleCount: examplesOf(nextRequest).length,
        rev: revOfRequest(nextRequest),
        note: 'The mock server is not startable from here — the user starts it in Apiboo. This example only exists on the request until they do.',
        scopesConsulted: [],
        scopesUnavailable: [],
      },
    };
  });
}

/**
 * Remove one example.
 *
 * **`destructive: true` since 24.09.2026 — a deliberate REVERSAL of PLAN §D2.**
 * §D2 left it outside the destructive set: a mock example is a sub-field of a
 * request the agent had probably just written, the call needs `expected_rev`
 * and names ONE example by an id it had to read first, so it looked nearer
 * `board_task_move` than `board_task_delete`.
 *
 * The cost that plan itself wrote down is why it was reversed: an example a
 * HUMAN wrote by hand — the one response body a mock server was built around —
 * could be removed, permanently and on every device, by an agent holding only
 * "Collections: write". Every other delete needs the write grant AND the
 * destructive switch; there is no principled line that makes this delete the
 * exception, and `expected_rev` guards against a stale target, not against an
 * unwanted delete. The flag lives in src-tauri/src/mcp/registry.rs; the switch
 * now unlocks twelve tools (mcp.destructiveEnableDesc says so in en + de).
 */
export async function mockExampleDelete(args = {}) {
  return applyCollectionsChange((collections) => {
    const found = findRequest(collections, args.request_id);
    if (!found) return { error: requestNotFound(args.request_id) };

    const stale = requireRev(args.expected_rev, revOfRequest(found.request), 'that request');
    if (stale) return { error: stale };

    const wanted = String(args.example_id ?? '');
    const example = examplesOf(found.request).find((ex) => String(ex?.id) === wanted);
    if (!example) {
      return {
        error: mcpError(
          'not_found',
          'That request has no mock example with that id. Nothing was written. Call request_read and use an id from mockExamples.',
          { requestId: String(found.request.id), exampleId: wanted }
        ),
      };
    }

    const nextRequest = {
      ...found.request,
      mock: {
        ...(found.request.mock || {}),
        responseExamples: examplesOf(found.request).filter((ex) => String(ex?.id) !== wanted),
      },
    };

    return {
      next: collections.map((c) =>
        String(c.id) === String(found.collection.id)
          ? {
              ...c,
              requests: (c.requests || []).map((r) =>
                String(r.id) === String(nextRequest.id) ? nextRequest : r
              ),
            }
          : c
      ),
      sizedRecords: [nextRequest],
      tabPatches: [{ requestId: nextRequest.id, patch: tabPatchFromRequest(nextRequest) }],
      result: {
        deleted: 'mock_example',
        collectionId: String(found.collection.id),
        requestId: String(nextRequest.id),
        exampleId: wanted,
        exampleCount: examplesOf(nextRequest).length,
        rev: revOfRequest(nextRequest),
        scopesConsulted: [],
        scopesUnavailable: [],
      },
    };
  });
}
